import { FunctionComponent, forwardRef } from 'react';
import { InputAdornment } from '@mui/material';
import { NumericFormat, NumericFormatProps } from 'react-number-format-v5';

import TextField, { TextFieldProps } from 'ui/TextField';

/**
 * Props we expect for the custom input component.
 * We omit `onValueChange` / `value` / `prefix` / `suffix` from NumericFormatProps because:
 * - onValueChange/value: we adapt them to MUI's onChange signature
 * - prefix/suffix: we handle these via InputAdornment, not NumericFormat's built-in formatting
 */
type NumberFormatCustomProps = Omit<
  NumericFormatProps,
  'onValueChange' | 'value' | 'prefix' | 'suffix'
> & {
  name?: string;
  onChange?: (event: { target: { name?: string; value: string } }) => void;
};

/**
 * NumericFormat wrapper that forwards refs correctly.
 */
const NumberFormatCustom = forwardRef<HTMLInputElement, NumberFormatCustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, name, ...other } = props;

    return (
      <NumericFormat
        {...(other as NumericFormatProps)}
        getInputRef={(instance: HTMLInputElement) => {
          // set both forwarded ref and MUI inputRef behavior
          if (ref) {
            if (typeof ref === 'function') {
              ref(instance);
            } else {
              (ref as React.MutableRefObject<HTMLInputElement | null>).current = instance;
            }
          }
        }}
        onValueChange={(values) => {
          onChange?.({
            target: {
              name,
              value: values.value,
            },
          });
        }}
        valueIsNumericString
      />
    );
  }
);

export interface NumericFieldProps extends TextFieldProps {
  /**
   * allow negative numbers, defaults to false
   */
  allowNegative?: boolean;
  /**
   * Number of decimal places allowed, defaults to 0
   */
  decimalScale?: number;
  /**
   * Matches decimal places to decimal scale, defaults to false
   */
  fixedDecimalScale?: boolean;
  /**
   * whether or not to have a `,` separator, defaults to undefined
   */
  thousandSeparator?: string | boolean;
  /**
   * string to place before the number, default is undefined
   */
  prefix?: string;
  /**
   * string to place after the number, default is undefined
   */
  suffix?: string;
  /**
   * symbol used to separate decimal places, default is '.'
   */
  decimalSeparator?: string;
}

/**
 * NumericField is a wrapper around TextField that integrates
 * [react-number-format](https://www.npmjs.com/package/react-number-format)
 * Only a subset of the props into react-number-format are exposed for simplicity.
 *
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const NumericField: FunctionComponent<React.PropsWithChildren<NumericFieldProps>> = ({
  allowNegative,
  decimalScale = 0,
  fixedDecimalScale = false,
  thousandSeparator,
  decimalSeparator = '.',
  prefix,
  suffix,
  InputProps,
  inputProps,
  ...rest
}) => {
  const startAdornment = prefix ? (
    <InputAdornment position="start">{prefix}</InputAdornment>
  ) : undefined;
  const endAdornment = suffix ? (
    <InputAdornment position="end">{suffix}</InputAdornment>
  ) : undefined;

  // merge caller's inputProps sensibly: caller props win but our numeric props are applied
  const mergedInputProps = {
    ...((InputProps && InputProps.inputProps) || {}),
    ...(inputProps || {}),
    allowNegative,
    decimalScale,
    fixedDecimalScale,
    thousandSeparator,
    decimalSeparator,
  };

  // Build InputProps. MUI v5's type system uses complex union types that TypeScript
  // cannot properly narrow, so we use a minimal type assertion here. This is a known
  // limitation when using custom input components with MUI v5.
  const finalInputProps = {
    ...InputProps,
    startAdornment,
    endAdornment,
    inputComponent: NumberFormatCustom,
    inputProps: mergedInputProps,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <TextField {...(rest as any)} InputProps={finalInputProps} />;
};

export default NumericField;
